/**
 * GetProductOffers Endpoint
 *
 * Fetches detailed offers for a specific product.
 * Used on product detail pages to show available booking options.
 */

import type { GetProductOffersResult } from '@/types/schemas';
import { playerClient } from './playerClient';
import { normalizeProductOffersResult } from '@/services/mappers/arrayNormalizers';
import {
  buildRequestHeader,
  buildPassengersXml,
  buildPeriodXml,
  buildRoomsXml,
  type PassengerConfig,
} from '@/utils/xml/xmlBuilder';
import { PLAYER_API_CONFIG } from '@/constants/player/config';

const { NAMESPACE } = PLAYER_API_CONFIG;

/**
 * Parameters for GetProductOffers endpoint
 *
 * `durations` widens the request from the single selected `duration` to a range,
 * which is what the second phase of the offer fetch sends when the selected
 * length came back empty. `preferredDurations` deliberately has no counterpart
 * here: the gateway's preference ranking is inert on this endpoint (the result
 * set is identical with and without it), so the ordering is ours to decide.
 */
export interface GetProductOffersParams extends PassengerConfig {
  startDate: string;
  endDate: string;
  duration: number;
  durations?: number[];
  productCode: string;
  resultsTotal?: number;
}

/**
 * Rows to ask the gateway for.
 *
 * The vendor sample's 300 already truncates a single-duration request: a stored
 * response reports `ResultsTotal=371` and carries exactly 300 variants. There is
 * no server-side cap, latency is flat and the payload is gzipped, so the floor is
 * raised outright and scaled by the number of requested lengths on top.
 */
function resolveResultsTotal(params: GetProductOffersParams): number {
  if (params.resultsTotal) {
    return params.resultsTotal;
  }
  return Math.max(1000, (params.durations?.length ?? 1) * 300);
}

/**
 * Creates SOAP request XML for GetProductOffers endpoint
 *
 * @param authKey - Authentication key
 * @param params - Offer request parameters
 * @returns SOAP envelope XML string
 */
function createGetProductOffersRequest(authKey: string, params: GetProductOffersParams): string {
  const passengersXml = buildPassengersXml({
    adults: params.adults,
    children: params.children,
    passengers: params.passengers,
  });

  const periodXml = buildPeriodXml({
    start: params.startDate,
    end: params.endDate,
    duration: params.duration,
    durations: params.durations,
  });

  const roomsXml = buildRoomsXml(params.rooms || []);

  return `
<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/"
                  xmlns:bewo="${NAMESPACE}"
                  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
   <soapenv:Header/>
   <soapenv:Body>
      <bewo:GetProductOffers>
         <bewo:GetProductOffersRequest>
            ${buildRequestHeader(authKey)}
            <bewo:Accommodation>
               <bewo:ProductCodes>
                  <bewo:ProductCode>${params.productCode}</bewo:ProductCode>
               </bewo:ProductCodes>
               <bewo:Identities/>
               <bewo:Name xsi:nil="true"/>
               <bewo:MinCategory xsi:nil="true"/>
               ${roomsXml}
               <bewo:Boards/>
            </bewo:Accommodation>
            <bewo:Flight>
               <bewo:DepartureAirports/>
               <bewo:ArrivalAirports/>
               <bewo:FlightNumbers/>
               <bewo:DepartureAirportsInbound xsi:nil="true"/>
               <bewo:ArrivalAirportsInbound xsi:nil="true"/>
            </bewo:Flight>
            <bewo:Extra xsi:nil="true"/>
            <bewo:Passengers>${passengersXml}
            </bewo:Passengers>
            ${periodXml}
            <bewo:GeoLocation xsi:nil="true"/>
            <bewo:GroupIds xsi:nil="true"/>
            <bewo:Price xsi:nil="true"/>
            <bewo:RequestBooking xsi:nil="true"/>
            <bewo:ResultsTotal>${resolveResultsTotal(params)}</bewo:ResultsTotal>
            <bewo:DetailLevel>PriceDetails</bewo:DetailLevel>
         </bewo:GetProductOffersRequest>
      </bewo:GetProductOffers>
   </soapenv:Body>
</soapenv:Envelope>`;
}

/**
 * Calls GetProductOffers API endpoint
 *
 * Fetches detailed booking offers for a specific product (hotel/accommodation).
 * Results include different room types, board options, prices, and availability.
 * Results are automatically normalized to ensure type-safe arrays.
 *
 * @param params - Offer parameters (product code, dates, passengers)
 * @param options - Optional configuration (signal for request cancellation)
 * @returns Object containing raw XML response and normalized result
 * @throws {PlayerApiError} On API errors or invalid responses
 *
 * @example
 * ```typescript
 * const { result } = await getProductOffers({
 *   productCode: "ABC123",
 *   startDate: "2024-07-15",
 *   endDate: "2024-07-22",
 *   duration: 7,
 *   adults: 2,
 *   children: [{ age: 8 }],
 *   resultsTotal: 100
 * });
 *
 * if (result?.Offers?.Offer) {
 *   const offers = result.Offers.Offer; // Type-safe array
 *   offers.forEach(offer => {
 *     console.log(offer.TotalPrice, offer.Board, offer.Room);
 *   });
 * }
 * ```
 *
 * @example With request cancellation
 * ```typescript
 * const controller = new AbortController();
 * const { result } = await getProductOffers(params, {
 *   signal: controller.signal
 * });
 * // controller.abort() to cancel request
 * ```
 */
export async function getProductOffers(
  params: GetProductOffersParams,
  options?: { signal?: AbortSignal }
): Promise<{
  raw: string;
  result: GetProductOffersResult | null;
}> {
  const requestBody = createGetProductOffersRequest('', params);

  const response = await playerClient.call({
    action: 'BewotecHubService/GetProductOffers',
    body: requestBody,
    signal: options?.signal,
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const parsed = response.parsed as any;
  const rawResult =
    parsed?.Envelope?.Body?.GetProductOffersResponse?.GetProductOffersResult || null;

  const result = rawResult
    ? normalizeProductOffersResult(rawResult as GetProductOffersResult)
    : null;

  return { raw: response.raw, result };
}
